import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { AppDispatch, RootState } from '../../../stateStore'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import leaveReportsApi from '../../../middleware/api/Leaves/LeaveReports/leaveReportApi'
import {
  LeaveReportsProps,
  SearchLeaveReportsProps,
  SelectFinancialYear,
  LeaveReportState,
  LeaveSummaries,
  LeaveSummariesList,
} from '../../../types/Leaves/LeaveReports/leaveReportTypes'

const getAllEmployeesLeaveSummaries = createAsyncThunk(
  'leaveReports/getAllEmployeesLeaveSummaries',
  async (props: LeaveReportsProps, thunkApi) => {
    try {
      return await leaveReportsApi.getAllEmployeesLeaveSummaries(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const searchLeaveSummaries = createAsyncThunk(
  'leaveReports/searchLeaveSummaries',
  async (props: SearchLeaveReportsProps, thunkApi) => {
    try {
      return await leaveReportsApi.searchLeaveSummaries(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const creditedYearDetails = createAsyncThunk<
  SelectFinancialYear[] | undefined,
  void,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('leaveReports/creditedYearDetails', async (_, thunkApi) => {
  try {
    return await leaveReportsApi.creditedYearDetails()
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getFinancialYear = createAsyncThunk<
  number | undefined,
  string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('leaveReports/financialYear', async (country: string, thunkApi) => {
  try {
    return await leaveReportsApi.getFinancialYear(country)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const initialLeaveReportState: LeaveReportState = {
  leaveSummaries: { list: [], size: 0, name: '', length: 0 },
  selectFinancialYear: [],
  isLoading: ApiLoadingState.idle,
  error: null,
  financialYear: 0,
  listSize: 0,
  isLoadingGetSummary: ApiLoadingState.idle,
}

const leaveReportSlice = createSlice({
  name: 'leaveReports',
  initialState: initialLeaveReportState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(getAllEmployeesLeaveSummaries.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.isLoadingGetSummary = ApiLoadingState.succeeded
        state.leaveSummaries = action.payload
        state.listSize = action.payload.size
      })
      .addCase(creditedYearDetails.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.selectFinancialYear = action.payload as SelectFinancialYear[]
      })
      .addCase(getFinancialYear.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.financialYear = action.payload as number
      })
      .addCase(getFinancialYear.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getAllEmployeesLeaveSummaries.pending, (state) => {
        state.isLoadingGetSummary = ApiLoadingState.loading
      })
      .addCase(searchLeaveSummaries.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.leaveSummaries = action.payload
      })
      .addMatcher(
        isAnyOf(
          getAllEmployeesLeaveSummaries.pending,
          creditedYearDetails.pending,
          getFinancialYear.pending,
          searchLeaveSummaries.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
  },
})

const leaveReportThunk = {
  getAllEmployeesLeaveSummaries,
  searchLeaveSummaries,
  creditedYearDetails,
  getFinancialYear,
}

const isLoading = (state: RootState): LoadingState =>
  state.leaveReport.isLoading

const isLoadingGetSummary = (state: RootState): LoadingState =>
  state.leaveReport.isLoadingGetSummary

const getLeaveReport = (state: RootState): LeaveSummaries =>
  state.leaveReport.leaveSummaries

const getLeaveSummariesList = (state: RootState): LeaveSummariesList[] =>
  state.leaveReport.leaveSummaries.list

const creditedYears = (state: RootState): SelectFinancialYear[] =>
  state.leaveReport.selectFinancialYear

const financialYear = (state: RootState): number =>
  state.leaveReport.financialYear

const listSize = (state: RootState): number => state.leaveReport.listSize

const leaveReportSelectors = {
  getLeaveReport,
  creditedYears,
  isLoading,
  financialYear,
  listSize,
  getLeaveSummariesList,
  isLoadingGetSummary,
}

export const leaveReportService = {
  ...leaveReportThunk,
  actions: leaveReportSlice.actions,
  selectors: leaveReportSelectors,
}

export default leaveReportSlice.reducer
